/**
 * PHI MASK — on/off state, and the localStorage caches that bypass the net.
 *
 * Split out from src/utils/phiMask.js so that file can stay pure and be loaded
 * directly by the node-tests. Everything Vue-, storage- or store-aware lives
 * here.
 *
 * TWO INDEPENDENT SWITCHES, OR'd TOGETHER
 *
 *   1. PER-USER (localStorage `teamio_phi_mask`) — a Super-Admin-only toggle in
 *      the header. This is the one to use for recording. It masks YOUR session
 *      only, so flipping it on a live practice's site does not show that
 *      practice's own staff fake patient names while they are trying to work.
 *
 *   2. PER-SITE (`GENERAL_DEMO_MODE_ON` in teamio.settings) — the original 2023
 *      flag, kept so existing demo/sandbox sites keep behaving as before.
 *
 * ABSENT => OFF, ALWAYS.
 *   This is the inverse of the usual default-ON convention for new settings in
 *   this codebase, and it is deliberate. A bug that fails OPEN here shows real
 *   PHI in a demo — bad. A bug that fails CLOSED masks a live practice's data
 *   and breaks their actual work — worse, and much harder to notice. Neither
 *   switch is ever inferred; both must be explicitly true.
 */

import Vue from 'vue';

export const LS_KEY = 'teamio_phi_mask';

/**
 * localStorage caches holding PATIENT data that are read back WITHOUT going
 * through axios — so the response interceptor never sees them.
 *
 * `patients` is the one that matters: store/customer.js:91 seeds the Vuex
 * patient roster directly from `JSON.parse(localStorage.getItem('patients'))`
 * at module init. That roster feeds the global Header patient search, the
 * Campaigns audience builder, the Conversations "start conversation" picker and
 * the Forms patient filter. Without purging it, turning the mask ON would leave
 * every typeahead in the app showing REAL patient names — exactly the silent
 * gap this rewrite exists to close.
 *
 * NOT listed here (checked, and deliberately kept): `employees`, `sites`,
 * `lists`, `providers`, `settings`, `procCodes` — none are patient data — and
 * `convV2LastRead:*`, which stores read timestamps only.
 */
export const PHI_CACHE_KEYS = ['patients'];

/** Records which mask state the caches above were written under. */
const CACHE_STAMP_KEY = 'teamio_phi_mask_cache_state';

function readLocalFlag() {
   try {
      return window.localStorage.getItem(LS_KEY) === '1';
   } catch (e) {
      // Private mode / storage disabled — fail CLOSED (no masking).
      return false;
   }
}

/* Vue.observable so the header toggle and the pill re-render, while the axios
 * interceptor can still read it synchronously outside any component. */
const state = Vue.observable({
   userMask: readLocalFlag(),
   siteMask: false,
});

/** Drop the PHI caches and stamp them with the state they'll be refilled under. */
function purgePhiCaches(maskedNow) {
   try {
      for (let i = 0; i < PHI_CACHE_KEYS.length; i++) {
         window.localStorage.removeItem(PHI_CACHE_KEYS[i]);
      }
      window.localStorage.setItem(CACHE_STAMP_KEY, maskedNow ? '1' : '0');
   } catch (e) {
      /* storage disabled — nothing cached to purge */
   }
}

/**
 * Reconcile the PHI caches with the current mask state, at MODULE LOAD.
 *
 * This module is imported BY store/customer.js, so this runs before
 * customer.js:91 seeds Vuex from `localStorage.getItem('patients')` — which is
 * the only reason the fix works. It catches what setUserMask() cannot:
 *   - the cache was written REAL in an earlier session and the mask is on now
 *     (a leak: real names in every typeahead)
 *   - the cache was written FAKE while masked and the mask is off now
 *     (fake names shown as if real)
 *   - the flag was flipped in another tab, or storage was edited
 *
 * A missing stamp — first run after this shipped, or a cache left by an older
 * build — is treated as a mismatch, so the very first load purges once.
 */
function reconcilePhiCaches() {
   try {
      const want = state.userMask ? '1' : '0';
      if (window.localStorage.getItem(CACHE_STAMP_KEY) === want) return;
      purgePhiCaches(state.userMask);
   } catch (e) {
      /* storage disabled — nothing cached to reconcile */
   }
}

reconcilePhiCaches();

/** The single question everything else asks. */
export function isMaskOn() {
   return state.userMask === true || state.siteMask === true;
}

/** Reactive handle for components (`maskState().userMask` in a computed). */
export function maskState() {
   return state;
}

/**
 * Header toggle target. Persists so it survives the reload that follows.
 * Callers MUST reload afterwards (Header.vue::togglePhiMask does): data already
 * in the Vuex store was fetched before the flip and is still in its old state,
 * and a half-masked screen is the worst thing to start recording against.
 */
export function setUserMask(on) {
   state.userMask = !!on;
   try {
      if (on) window.localStorage.setItem(LS_KEY, '1');
      else window.localStorage.removeItem(LS_KEY);
   } catch (e) {
      /* storage disabled — in-memory only for this session */
   }
   purgePhiCaches(!!on);
}

/**
 * Called by store/customer.js::setSettings once settings load, and again on
 * reload. Accepts the raw `settings` array ([{Property, PropertyValue}, ...]).
 * Matches the string-boolean convention used by every other flag in this repo.
 *
 * If the SITE flag has just turned masking on while the per-user toggle is off,
 * the patient cache on disk was written unmasked — purge it so the next load is
 * clean. (The current load already read it; that one-load lag only affects the
 * legacy site-wide path, not the per-user toggle used for recording.)
 */
export function syncSiteMask(settings) {
   let on = false;
   if (Array.isArray(settings)) {
      for (let i = 0; i < settings.length; i++) {
         const s = settings[i];
         if (s && s.Property === 'GENERAL_DEMO_MODE_ON') {
            on = String(s.PropertyValue || '').toUpperCase() === 'TRUE';
            break;
         }
      }
   }
   const was = isMaskOn();
   state.siteMask = on;
   if (isMaskOn() !== was) purgePhiCaches(isMaskOn());
   return on;
}
